import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, FileText } from "lucide-react";
import { GRIEVANCE_FORM_URL } from "../components/LegalLinks";
import logo from "../assets/twu-logo.png";

function Section({ title, children }) {
  return (
    <section className="mb-8" data-testid={`terms-section-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <h2 className="font-head text-lg font-extrabold tracking-tight mb-2">{title}</h2>
      <div className="text-sm leading-relaxed text-muted-foreground space-y-2">{children}</div>
    </section>
  );
}

export default function SmsTerms() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center gap-4">
          <img src={logo} alt="Transport Workers Union Local 555" className="w-16 h-auto" />
          <div>
            <div className="font-head font-extrabold text-sm leading-none tracking-tight">TWU LOCAL 555 BNA</div>
            <div className="text-[10px] tracking-[0.3em] text-muted-foreground mt-1">GRIEVANCE TRACKER</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="sms-terms">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-2">
          <FileText className="w-4 h-4" /> SMS Terms &amp; Conditions
        </p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">
          TWU Local 555 BNA Grievance Tracker SMS Terms &amp; Conditions
        </h1>
        <p className="text-sm text-muted-foreground mt-2" data-testid="terms-effective-date">Effective Date: July 26, 2026</p>

        <div className="h-px bg-border my-8" />

        <Section title="SMS Terms & Conditions">
          <p>
            By opting in to receive SMS (text) messages from the TWU Local 555 BNA Grievance Tracker, you agree to
            these Terms &amp; Conditions.
          </p>
        </Section>

        <Section title="Program Description">
          <p>
            The TWU Local 555 BNA Grievance Tracker SMS program provides text message notifications related to your
            grievance, including but not limited to:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Grievance status updates</li>
            <li>Requests for additional information or documentation</li>
            <li>Meeting, hearing, or deadline reminders</li>
            <li>General communications regarding your grievance</li>
          </ul>
        </Section>

        <div className="mb-8 border-2 border-primary rounded-sm p-4 bg-secondary/40" data-testid="terms-voluntary-callout">
          <p className="text-sm leading-relaxed text-foreground">
            <strong>Consent to receive text messages is completely voluntary and optional.</strong> It is
            <strong> not</strong> a condition of filing a grievance, receiving union representation, maintaining union
            membership, creating an account, or using the TWU Local 555 BNA Grievance Tracker in any way. You may use
            all services without ever opting in to SMS, and you may opt out at any time.
          </p>
        </div>

        <Section title="How You Opt In (Consent)">
          <p>
            Enrollment in the SMS program is always initiated by you through an affirmative, voluntary action. We obtain
            your express consent through one or both of the following opt-in methods:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong className="text-foreground">The official grievance form.</strong> When you file your grievance on
              the TWU Local 555 grievance form, the form includes a separate, unchecked-by-default SMS consent checkbox.
              You must actively check that box to opt in — submitting a grievance does not require it, and your grievance
              is filed and processed whether or not you opt in. You can view the form here:{" "}
              <a href={GRIEVANCE_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="terms-grievance-form-link"
                className="text-foreground underline underline-offset-2 hover:no-underline">
                TWU Local 555 Grievance Form (PDF)
              </a>.
            </li>
            <li>
              <strong className="text-foreground">Verbal or written request to your Grievance Representative.</strong>{" "}
              You provide your mobile phone number and expressly ask to receive grievance-related text updates for your
              specific case.
            </li>
          </ul>
          <p>
            Consent applies only to this program — grievance-related communications from the TWU Local 555 BNA Grievance
            Tracker — and to the specific grievance case for which you provided your number. A representative example of
            the consent language you agree to is:
          </p>
          <p className="border-l-2 border-border pl-3 italic text-foreground">
            "I agree to receive text messages from the TWU Local 555 BNA Grievance Tracker about my grievance at the
            mobile number I provided. Consent is not a condition of any service. Message frequency varies. Message and
            data rates may apply. Reply STOP to opt out or HELP for help."
          </p>
        </Section>

        <Section title="User Consent">
          <p>
            By providing your mobile phone number and completing one of the opt-in methods above, you expressly consent
            to receive SMS messages from the TWU Local 555 BNA Grievance Tracker regarding your grievance.
          </p>
          <p>
            Your consent to receive text messages is voluntary and is not a condition of union membership,
            representation, filing or processing a grievance, or any other service. Declining or withdrawing SMS consent
            will not affect your grievance or your representation in any way.
          </p>
        </Section>

        <Section title="Message Frequency">
          <p>
            Message frequency varies based on the activity and status of your grievance. You will receive messages only
            as necessary to communicate updates or requests related to your grievance.
          </p>
        </Section>

        <Section title="Message and Data Rates">
          <p>
            Message and data rates may apply. Charges are determined by your wireless carrier and service plan. Contact
            your mobile provider for information about your messaging and data rates.
          </p>
        </Section>

        <Section title="Opt-Out Instructions">
          <p>
            You may opt out of receiving SMS messages at any time by replying <strong className="text-foreground">STOP</strong> to
            any message.
          </p>
          <p>
            After opting out, you will receive one final confirmation message, and no further SMS messages will be sent
            unless you opt in again.
          </p>
        </Section>

        <Section title="Help Instructions">
          <p>
            For assistance with the SMS program, reply <strong className="text-foreground">HELP</strong> to any message or
            contact your TWU Local 555 BNA Grievance Representative.
          </p>
        </Section>

        <Section title="Eligibility">
          <p>
            You must be the authorized user of the mobile phone number provided or have permission from the account
            holder to enroll in this SMS program.
          </p>
        </Section>

        <Section title="Privacy">
          <p>
            Your privacy is important to us. Your mobile phone number and SMS consent information are used solely to
            provide grievance-related communications.
          </p>
          <p>
            No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.
            Text messaging originator opt-in data and consent will not be shared with any third parties, except with
            service providers that assist in delivering our text messages.
          </p>
          <p>
            Please review our{" "}
            <Link to="/sms-privacy-policy" data-testid="terms-privacy-link" className="text-foreground underline underline-offset-2 hover:no-underline">
              Privacy Policy
            </Link>{" "}
            for additional information about how your information is collected, used, and protected.
          </p>
        </Section>

        <Section title="Carrier Disclaimer">
          <p>
            Wireless carriers are not liable for delayed or undelivered messages. Delivery of SMS messages depends on
            your wireless carrier and network availability.
          </p>
        </Section>

        <Section title="Changes to These Terms">
          <p>
            TWU Local 555 BNA Grievance Tracker reserves the right to modify these Terms &amp; Conditions at any time.
            Updated terms will be posted with a revised Effective Date.
          </p>
        </Section>

        <Section title="Contact Information">
          <p>If you have questions regarding these Terms &amp; Conditions or the SMS messaging program, please contact:</p>
          <div className="mt-2 border border-border bg-card rounded-sm p-4 text-foreground">
            <div className="font-medium">TWU Local 555 BNA Grievance Tracker</div>
            <a href="mailto:bna.ramp@twu555.org" data-testid="terms-contact-email"
              className="mt-2 flex items-center gap-2 text-sm hover:underline underline-offset-2">
              <Mail className="w-4 h-4" /> bna.ramp@twu555.org
            </a>
            <a href="tel:+13049935996" data-testid="terms-contact-phone"
              className="mt-1 flex items-center gap-2 text-sm hover:underline underline-offset-2">
              <Phone className="w-4 h-4" /> 304-993-5996
            </a>
          </div>
        </Section>

        <div className="h-px bg-border my-8" />
        <Link to="/" data-testid="terms-back-home"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Grievance Tracker
        </Link>
      </main>
    </div>
  );
}
